import { Injectable } from '@angular/core';
import { spriMockApiHandler } from '@spri/lib/mock-api/mock-api.request-handler';
import type { spriMockApiMethods } from '@spri/lib/mock-api/mock-api.types';
import { compact, fromPairs } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class SpriMockApiService {
  private handlers: Record<string, Map<string, spriMockApiHandler>> = {
    get: new Map<string, spriMockApiHandler>(),
    post: new Map<string, spriMockApiHandler>(),
    patch: new Map<string, spriMockApiHandler>(),
    delete: new Map<string, spriMockApiHandler>(),
    put: new Map<string, spriMockApiHandler>(),
    head: new Map<string, spriMockApiHandler>(),
    jsonp: new Map<string, spriMockApiHandler>(),
    options: new Map<string, spriMockApiHandler>(),
  };

  /**
   * Find the handler from the service
   * with the given method and url
   *
   * @param method
   * @param url
   */
  public findHandler(
    method: string,
    url: string
  ): {
    handler: spriMockApiHandler | undefined;
    urlParams: Record<string, string>;
  } {
    // Prepare the return object
    const matchingHandler: {
      handler: spriMockApiHandler | undefined;
      urlParams: Record<string, string>;
    } = {
      handler: undefined,
      urlParams: {},
    };

    // Split the url
    const urlParts = url.split('/');

    // Get all related request handlers
    const handlers = this.handlers[method.toLowerCase()];

    // Iterate through the handlers
    handlers.forEach((handler, handlerUrl) => {
      // Skip if there is already a matching handler
      if (matchingHandler.handler) {
        return;
      }

      // Split the handler url
      const handlerUrlParts = handlerUrl.split('/');

      // Skip if the lengths of the urls we are comparing are not the same
      if (urlParts.length !== handlerUrlParts.length) {
        return;
      }

      // Compare
      const matches = handlerUrlParts.every(
        (handlerUrlPart, index) => handlerUrlPart === urlParts[index] || handlerUrlPart.startsWith(':')
      );

      // If there is a match...
      if (matches) {
        // Assign the matching handler
        matchingHandler.handler = handler;

        // Extract and assign the parameters
        matchingHandler.urlParams = fromPairs(
          compact(
            handlerUrlParts.map((handlerUrlPart, index) =>
              handlerUrlPart.startsWith(':') ? [handlerUrlPart.substring(1), urlParts[index]] : undefined
            )
          )
        );
      }
    });

    return matchingHandler;
  }

  /**
   * Register GET request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onGet(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('get', url, delay);
  }

  /**
   * Register POST request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onPost(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('post', url, delay);
  }

  /**
   * Register PATCH request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onPatch(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('patch', url, delay);
  }

  /**
   * Register DELETE request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onDelete(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('delete', url, delay);
  }

  /**
   * Register PUT request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onPut(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('put', url, delay);
  }

  /**
   * Register HEAD request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onHead(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('head', url, delay);
  }

  /**
   * Register JSONP request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onJsonp(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('jsonp', url, delay);
  }

  /**
   * Register OPTIONS request handler
   *
   * @param url - URL address of the mocked API endpoint
   * @param delay - Delay of the response in milliseconds
   */
  public onOptions(url: string, delay?: number): spriMockApiHandler {
    return this.registerHandler('options', url, delay);
  }

  /**
   * Register and return a new instance of the handler
   *
   * @param method
   * @param url
   * @param delay
   * @private
   */
  private registerHandler(method: spriMockApiMethods, url: string, delay?: number): spriMockApiHandler {
    // Create a new instance of spriMockApiRequestHandler
    const spriMockHttp = new spriMockApiHandler(url, delay);

    // Store the handler to access it from the interceptor
    this.handlers[method].set(url, spriMockHttp);

    // Return the instance
    return spriMockHttp;
  }
}
